import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Token storage strategy (pragmatic compromise — see security report):
 * - access_token  -> sessionStorage (cleared on tab close, smaller XSS exposure)
 * - refresh_token -> localStorage   (allows silent re-auth after reload)
 * - user          -> localStorage   (UI hydration only; backend always re-validates)
 *
 * Access tokens are short-lived (30 min). On 401 we attempt a silent refresh
 * once before redirecting the user to /login.
 */

const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';
const USER_KEY = 'user';

const getAccessToken = () => sessionStorage.getItem(ACCESS_KEY);
const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Silent refresh handling ---------------------------------------------
// While a refresh is in flight, queue any other 401-failing requests and
// retry them once the refresh resolves. This avoids a thundering herd of
// concurrent refresh calls.
let refreshPromise = null;

async function performRefresh() {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  try {
    const res = await axios.post(
      `${BACKEND_URL}/api/auth/refresh`,
      null,
      { params: { refresh_token: refresh } }
    );
    const { access_token, refresh_token } = res.data;
    sessionStorage.setItem(ACCESS_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
    return access_token;
  } catch (err) {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Don't try to refresh on the refresh endpoint itself, on login, or
    // when we've already retried once.
    const isAuthEndpoint =
      original?.url?.includes('/auth/login') ||
      original?.url?.includes('/auth/refresh') ||
      original?.url?.includes('/auth/logout');

    // FastAPI's default HTTPBearer returns 403 when the Authorization header
    // is missing/invalid, and 401 when the token is expired. We try silent
    // refresh on either status. However, after the retry we ONLY redirect to
    // /login on 401 — a persistent 403 means the user is authenticated but
    // lacks permission for that endpoint (e.g. a CLIENT calling an
    // ADMIN-only route). In that case we just propagate the error to the
    // caller so the page can render its own "no access" state.
    const needsRefresh = (status === 401 || status === 403);

    if (needsRefresh && !original?._retry && !isAuthEndpoint) {
      original._retry = true;

      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
    }

    // Only force re-login when the token itself is invalid (401).
    if (status === 401 && !isAuthEndpoint) {
      clearAuthTokens();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const setAuthTokens = (accessToken, refreshToken) => {
  sessionStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_KEY, refreshToken);
  }
};

export const clearAuthTokens = () => {
  sessionStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
};

export const getStoredUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

export const setStoredUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const isAuthenticated = () => {
  // Either a live access token or a refresh token we can use silently.
  return !!getAccessToken() || !!getRefreshToken();
};

export default api;
